import React, { useState, useMemo } from 'react';

import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { InputText } from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';

import HotelIcon from '@material-ui/icons/Hotel';
import BathtubIcon from '@material-ui/icons/Bathtub';
import PeopleIcon from '@material-ui/icons/People';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import InfoIcon from '@material-ui/icons/Info';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { ReservationMode } from '../../../../../shared/enums/reservation-mode.enum';
import Select, { ValueType } from 'react-select';
// import { Calendar } from '../../../../../shared/components/widgets/calendar/calendar.component';
import {
  getAllUnitTypes
  // searchInUnitTypes
} from '../../../../../shared/services/data/unit-type.data';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getModuleName } from '../../../../shared/services/reservation.service';
import { UnitTypeGroupUiModel } from '../../../../../shared/ui-models/unit-type-group.ui-model';
import { WEBSITE_HOME } from '../../../../../shared/config/routes-config';
import uniqBy from 'lodash/uniqBy';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { NotFoundData } from '../../../../../ui/NotFoundData';
export interface ChooseUnitTypeProps {
  data: {
    allAreas: {
      value: string;
      label: string;
      areaId: string;
    }[];
    allProjects: {
      value: string;
      label: string;
      areaId: string;
    }[];

    unitTypeGroups: UnitTypeGroupUiModel[];
  };
  eventHandlers: {
    selectedUnitType: (unitType: UnitTypeUiModel) => void;
  };
}

// Helper function to check if unit matches area filter
const matchesArea = (unit: UnitTypeUiModel, areaValue: string): boolean => {
  return !areaValue || unit.area?.includes(areaValue) || false;
};

// Helper function to check if unit matches project filter
const matchesProject = (unit: UnitTypeUiModel, projectValue: string): boolean => {
  return !projectValue || unit.propertyId?.includes(projectValue) || false;
};

// Helper function to check if unit has matching room/unit type
const hasMatchingRoom = (unit: UnitTypeUiModel, unitTypeValue: string): boolean => {
  if (!unitTypeValue) return true;
  return unit?.units?.some((room) => room?.unitId?.includes(unitTypeValue)) || false;
};

// Helper function to check if unit matches landlord filter
const matchesLandlord = (unit: UnitTypeUiModel, userValue: string): boolean => {
  return !userValue || unit.landlordId?.includes(userValue) || false;
};

// Helper function to check if unit matches unit type group filter
const matchesUnitTypeGroup = (unit: UnitTypeUiModel, unitTypeGroupValue: string): boolean => {
  return !unitTypeGroupValue || unit.unitTypeGroupId?.includes(unitTypeGroupValue) || false;
};

// Helper function to check if unit/room matches search text
const matchesSearchText = (unit: UnitTypeUiModel, searchValue: string): boolean => {
  if (!searchValue) return true;
  const lowerSearchValue = searchValue.toLowerCase();

  return (
    unit.name?.toLowerCase().includes(lowerSearchValue) ||
    unit.area?.toLowerCase().includes(lowerSearchValue) ||
    unit.landlordUsername?.toLowerCase().includes(lowerSearchValue) ||
    unit.propertyName?.toLowerCase().includes(lowerSearchValue) ||
    (unit?.units?.some(
      (room) =>
        room.shortName?.toLowerCase().includes(lowerSearchValue) ||
        room.roomNumber?.toLowerCase().includes(lowerSearchValue) ||
        room.unitId?.toLowerCase().includes(lowerSearchValue)
    ) ??
      false)
  );
};

// Main filter function combining all filter criteria
const applyFilters = (
  units: UnitTypeUiModel[],
  filters: {
    area: string;
    project: string;
    unitType: string;
    landlord: string;
    unitTypeGroup: string;
    searchText: string;
  }
): UnitTypeUiModel[] => {
  return units.filter((unit) => {
    return (
      matchesArea(unit, filters.area) &&
      matchesProject(unit, filters.project) &&
      hasMatchingRoom(unit, filters.unitType) &&
      matchesLandlord(unit, filters.landlord) &&
      matchesUnitTypeGroup(unit, filters.unitTypeGroup) &&
      matchesSearchText(unit, filters.searchText)
    );
  });
};

export const ChooseUnitType: React.FC<ChooseUnitTypeProps> = ({
  data,
  eventHandlers
}: ChooseUnitTypeProps) => {
  const [unitTypeSearch, setUnitTypeSearch] = useState<FormElementData<string>>({
    value: '',
    isValid: true
  });
  const [unitTypes, setUnitTypes] = useState<UnitTypeUiModel[]>([]);
  const [originalUnitTypes, setOriginalUnitTypes] = useState<UnitTypeUiModel[]>([]);

  // const [usersOptions, setUsersOptions] = useState<
  //   {
  //     value: string;
  //     label: string;
  //     roomId: { unitId: string; shortName: string; isActive: boolean; roomNumber: string }[];
  //     propertyId: string;
  //     areaId: string;
  //     groupId: string;
  //   }[]
  // >([]);

  const [groupOptions, setGroupOptions] = useState<
    {
      id: string;
      name: string;
      roomId: { unitId: string; shortName: string; isActive: boolean; roomNumber: string }[];
      propertyId: string;
      areaId: string;
      userId: string;
    }[]
  >([]);
  const [unitTypeOptions, setUnitTypeOptions] = useState<
    {
      value: string;
      label: string;
      propertyId: string;
      userId: string;
      areaId: string;
      groupId: string;
    }[]
  >([]);

  const [area, setArea] = useState<{ value: string; label: string; areaId: string }>({
    value: '',
    label: '',
    areaId: ''
  });
  const [project, setProject] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [unitType, setUnitType] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [unitTypeGroup, setUnitTypeGroup] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [user, setUser] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  // const [date, setDate] = useState<Date[]>([]);

  const projectOptions = useMemo(() => {
    if (area.value) {
      return data.allProjects?.filter((p) => p.areaId === area.areaId);
    }
    return data.allProjects;
  }, [area]);

  const unitTypesOptions = useMemo(() => {
    if (user.value) {
      return groupOptions?.filter((unit) => unit?.userId?.includes(user.value));
    }
    if (!unitType.value) {
      return groupOptions?.length > 0 ? groupOptions : data.unitTypeGroups;
    }

    return groupOptions?.filter((unit) =>
      unit?.roomId?.reduce((acc, room) => {
        if (room?.unitId?.includes(unitType.value)) {
          return true;
        } else {
          return acc || false;
        }
      }, false)
    );
  }, [unitType, groupOptions, user]);

  const roomsOptions = useMemo(() => {
    if (user.value) {
      return unitTypeOptions?.filter((room) => room?.userId?.includes(user.value));
    }

    return unitTypeOptions;
  }, [user, unitTypeOptions]);

  function fetchUnitTypesByProjectId(projectId: string) {
    const promise = getAllUnitTypes(getModuleName(), 0, -1, {
      propertyId: projectId,
      isManaged: true
    }).then((response) => {
      if (!isErrorInfo(response)) {
        const units = response?.unitList || [];
        setUnitTypes(units);
        setOriginalUnitTypes(units);

        // const usersTemp = units
        //   .filter((u) => u?.landlordId && u?.landlordUsername)
        //   .map((u) => ({
        //     value: u.landlordId!,
        //     label: u.landlordUsername!,
        //     roomId: u.units || [],
        //     areaId: u.areaId || '',
        //     propertyId: u.propertyId || '',
        //     groupId: u.unitTypeGroupId || ''
        //   }));

        const roomsTemp = units
          .filter((u) => u?.units && u.units.length > 0)
          .flatMap((u) =>
            u.units!.map((room) => ({
              value: room.unitId,
              label: room.shortName,
              propertyId: u.propertyId || '',
              userId: u.landlordId || '',
              areaId: u.areaId || '',
              groupId: u.unitTypeGroupId || ''
            }))
          );

        const unitTypeGroupsTemp = units
          .filter((u) => u?.unitTypeGroupId)
          .map((u) => ({
            id: u.unitTypeGroupId!,
            name: data.unitTypeGroups?.find((group) => group.id === u.unitTypeGroupId)?.name || '',
            roomId: u.units || [],
            areaId: u.areaId || '',
            propertyId: u.propertyId || '',
            userId: u.landlordId || ''
          }));

        setGroupOptions(uniqBy(unitTypeGroupsTemp, 'id'));
        setUnitTypeOptions(uniqBy(roomsTemp, 'value'));
        // setUsersOptions(uniqBy(usersTemp, 'value'));
      }
    });
    birdToast.promise(promise, {
      loadingMessage: 'Loading available units...',
      successMessage: 'Units loaded successfully',
      errorFailedMessage: 'Failed to load units'
    });
  }

  // const searchUnitTypeByDate = (date: Date[]) => {
  //   const promise = searchInUnitTypes(getModuleName(), {
  //     checkInDate: date[0].getTime(),
  //     checkOutDate: date[1].getTime()
  //   }).then((response: UnitTypeUiModel[] | ErrorInfo) => {
  //     if (!isErrorInfo(response)) {
  //       // Filter results to match the selected project (property)
  //       let availableUnits = response || [];

  //       if (project.value) {
  //         availableUnits = availableUnits.filter((unit) =>
  //           unit.propertyId?.includes(project.value)
  //         );
  //       }

  //       const filteredUnits = applyFilters(availableUnits, {
  //         area: area.value,
  //         project: project.value,
  //         unitType: unitType.value,
  //         landlord: user.value,
  //         unitTypeGroup: unitTypeGroup.value,
  //         searchText: unitTypeSearch.value
  //       });

  //       setUnitTypes(filteredUnits);
  //     } else {
  //       setUnitTypes([]);
  //     }
  //   });

  //   birdToast.promise(promise, {
  //     loadingMessage: 'Searching available units by date...',
  //     successMessage: 'Units loaded successfully',
  //     errorFailedMessage: 'Failed to search units by date'
  //   });
  // };

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: state.isDisabled ? '#d1d5db' : state.isFocused ? '#237c58' : '#eaeaea',
      backgroundColor: state.isDisabled ? '#f3f4f6' : '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: state.isFocused ? '0px 1px 3px 0px rgba(0,0,0,0.3)' : 'none',
      padding: '0.25rem',
      fontSize: '0.8125rem',
      transition: 'all 0.2s ease',
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: state.isDisabled ? '#d1d5db' : '#237c58'
      }
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#237c58' : state.isFocused ? '#e9f2ee' : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#292a2b',
      cursor: 'pointer',
      fontWeight: state.isSelected ? '600' : '400'
    }),
    menu: (provided: any) => ({
      ...provided,
      boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.3), 0px 8px 12px 6px rgba(0,0,0,0.15)',
      borderRadius: '0.5rem',
      backgroundColor: '#ffffff'
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#707071',
      fontFamily: 'Gilroy-Regular, sans-serif',
      fontSize: '0.8125rem'
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#292a2b',
      fontFamily: 'Gilroy-Medium, sans-serif',
      fontSize: '0.8125rem'
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '0px 6px'
    })
  };

  const resultsMeta = useMemo(() => {
    const total = originalUnitTypes.length;
    const showing = unitTypes.length;

    if (total === 0) return null;

    return {
      total,
      showing
    };
  }, [originalUnitTypes.length, unitTypes.length]);

  const clearAllFilters = () => {
    setProject({ value: '', label: '' });
    setUnitType({ value: '', label: '' });
    setUser({ value: '', label: '' });
    setArea({ value: '', label: '', areaId: '' });
    setUnitTypeSearch({ value: '', isValid: true });
    setUnitTypes([]);
    setOriginalUnitTypes([]);
    setUnitTypeGroup({ value: '', label: '' });
    setGroupOptions([]);
    setUnitTypeOptions([]);
  };

  const runSearch = () => {
    if (originalUnitTypes.length === 0) return;
    const filteredUnits = applyFilters(originalUnitTypes, {
      area: area.value,
      project: project.value,
      unitType: unitType.value,
      landlord: user.value,
      unitTypeGroup: unitTypeGroup.value,
      searchText: unitTypeSearch.value
    });
    setUnitTypes(filteredUnits);
  };

  return (
    <div className="w-full h-full">
      {/* Search & Filter Section */}
      <div className="p-2.5 bg-white rounded-xl border border-neutral-100 shadow-sm mb-3">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2">
            <div className="flex-1 min-w-0">
              <Select
                value={area.value ? { value: area.value, label: area.label } : null}
                options={data.allAreas}
                styles={customSelectStyles}
                placeholder="Area"
                isClearable
                onChange={(value: any) => {
                  setProject({ value: '', label: '' });
                  setUnitTypes([]);
                  setOriginalUnitTypes([]);
                  setArea(value ? value : { value: '', label: '', areaId: '' });
                  setGroupOptions([]);
                  setUnitTypeOptions([]);
                  setUnitType({ value: '', label: '' });
                  setUnitTypeGroup({ value: '', label: '' });
                }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <Select
                isDisabled={area.value === ''}
                value={project.value ? { value: project.value, label: project.label } : null}
                styles={customSelectStyles}
                placeholder="Project"
                isClearable
                options={projectOptions}
                onChange={(
                  value: ValueType<{ value: string | undefined; label: string }, false>
                ) => {
                  const selectedProject = value?.value;
                  setProject(
                    value
                      ? { value: selectedProject || '', label: value.label }
                      : { value: '', label: '' }
                  );
                  setUnitTypes([]);
                  setOriginalUnitTypes([]);
                  setGroupOptions([]);
                  setUnitTypeOptions([]);
                  setUnitType({ value: '', label: '' });
                  setUnitTypeGroup({ value: '', label: '' });
                  fetchUnitTypesByProjectId(selectedProject || '');
                }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <Select
                isDisabled={unitTypeOptions.length === 0}
                value={unitType.value ? { value: unitType.value, label: unitType.label } : null}
                styles={customSelectStyles}
                placeholder="Room"
                isClearable
                options={roomsOptions.map((room) => ({
                  value: room.value,
                  label: room.label
                }))}
                onChange={(
                  value: ValueType<{ value: string | undefined; label: string }, false>
                ) => {
                  setUnitType(
                    value
                      ? { value: value.value || '', label: value.label }
                      : { value: '', label: '' }
                  );
                  setUnitTypeGroup({ value: '', label: '' });
                }}
              />
            </div>

            {resultsMeta && (
              <div className="text-xs font-gilroy-medium text-neutral-500 flex items-center flex-shrink-0 whitespace-nowrap">
                Showing <span className="text-neutral-700 ml-1">{resultsMeta.showing}</span> of{' '}
                <span className="text-neutral-700 ml-1">{resultsMeta.total}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2">
            <div className="flex-1 min-w-0">
              <Select
                value={
                  unitTypeGroup.value
                    ? { value: unitTypeGroup.value, label: unitTypeGroup.label }
                    : null
                }
                styles={customSelectStyles}
                placeholder="Unit Type"
                isClearable
                options={
                  unitTypesOptions.map((group) => ({
                    value: group.id || '',
                    label: group.name || ''
                  })) as any
                }
                onChange={(value: any) => {
                  setUnitTypeGroup(
                    value
                      ? { value: value.value || '', label: value.label }
                      : { value: '', label: '' }
                  );
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <InputText
                data={{
                  value: unitTypeSearch.value,
                  placeholder: 'Search units...'
                }}
                config={{
                  maxRows: 1,
                  autoFocus: true
                }}
                eventHandlers={{
                  onChangeHandler: (value: string, isValid: boolean) => {
                    setUnitTypeSearch({ value, isValid });
                  }
                }}
              />
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Button size={ButtonSize.small} type={ButtonType.gray} onClick={clearAllFilters}>
                Clear
              </Button>
              <Button
                size={ButtonSize.small}
                disabled={originalUnitTypes.length === 0}
                onClick={runSearch}
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {unitTypes?.map((unit) => (
          <div
            key={unit.id}
            onClick={() => eventHandlers.selectedUnitType(unit)}
            className="group bg-white rounded-2xl border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden  cursor-pointer flex flex-col"
          >
            {/* Image Section */}
            <div className="relative h-48 bg-gray-100 overflow-hidden">
              <img
                alt={unit.name || 'Unit'}
                src={unit.coverImage}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />

              {/* Status Badge (Top Right) */}
              <div className="absolute top-3 right-3 flex flex-row gap-2">
                <span className="px-2.5 py-3 rounded-lg bg-white/90  text-primary-600 text-base font-gilroy-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                  {unit.availableFrom
                    ? `Available from: ${new Date(unit.availableFrom).toLocaleDateString()}`
                    : 'Available'}
                </span>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(WEBSITE_HOME + 'unit/' + unit.id, '_blank');
                  }}
                  className="p-2 rounded-lg bg-white/90 w-[35px] hover:bg-primary-600 text-primary-600 hover:text-white transition-all duration-200 "
                  title="Open unit details on website"
                >
                  <OpenInNewIcon style={{ fontSize: 20 }} />
                </button>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-5 flex flex-col gap-2">
              {/* Header */}
              <div>
                {unit?.unitPrice ? (
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-xl font-gilroy-bold text-neutral-700 font-bold">
                      EGP {unit.unitPrice.toLocaleString()}
                    </span>
                    <span className="text-sm font-gilroy-medium opacity-90">
                      {unit.reservationMode === ReservationMode.MONTHLY
                        ? ' / Month + Utilities'
                        : ' / Night + Utilities'}
                    </span>
                  </div>
                ) : null}
                <h3 className="text-xl font-gilroy-medium text-neutral-900 mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {unit.name || 'Untitled Unit'}
                </h3>
                <div className="flex items-center text-neutral-500 text-lg font-gilroy-medium">
                  <span className="truncate">
                    {unit.propertyName} • {unit.area}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-neutral-100" />

              {/* Stats Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Bedrooms */}
                <div className="flex items-center gap-2">
                  <HotelIcon style={{ fontSize: 20 }} />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-base font-gilroy-bold text-neutral-900 truncate">
                      Bedrooms: {unit?.bedrooms || 0}
                    </span>
                  </div>
                </div>

                {/* Bathrooms */}
                <div className="flex items-center gap-2">
                  <BathtubIcon style={{ fontSize: 20 }} />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-base font-gilroy-bold text-neutral-900 truncate">
                      Bathrooms: {unit?.bathrooms || 0}
                    </span>
                  </div>
                </div>

                {/* Max Guests */}
                <div className="flex items-center gap-2">
                  <PeopleIcon style={{ fontSize: 20 }} />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-base font-gilroy-bold text-neutral-900 truncate">
                      Max guests: {unit?.maxGuests || 0}
                    </span>
                  </div>
                </div>

                {/* Area */}
                <div className="flex items-center gap-2">
                  <AspectRatioIcon style={{ fontSize: 20 }} />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-base font-gilroy-bold text-neutral-900 truncate">
                      Area: {unit?.unitMeters || 'N/A'} SQM
                    </span>
                  </div>
                </div>
              </div>

              {/* Room Tags */}
              <div className="flex flex-wrap gap-2 mt-auto pt-2">
                {unit.units && unit.units.length > 0 ? (
                  unit.units.slice(0, 3).map((room, i) => (
                    <span
                      key={i}
                      className=" inline-flex items-center p-1 rounded-lg font-gilroy-medium text-sm font-extrabold uppercase tracking-wide border bg-primary-500 text-primary-50 border-primary-200"
                    >
                      {room.shortName}
                    </span>
                  ))
                ) : (
                  <span className="text-neutral-400 text-xs italic">No specific rooms</span>
                )}
                {(unit.units?.length || 0) > 3 && (
                  <span className="px-2 py-1 text-neutral-400 text-sm font-gilroy-bold">
                    +{(unit.units?.length || 0) - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {unitTypes?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16">
          <NotFoundData />
        </div>
      )}
    </div>
  );
};
